import GeneralStatusLabel from "../../components/Common/GeneralStatusLabel";
import Helpers from "../../helpers/helpers";
import React, { useEffect, useState } from "react";
import { IconButton, Stack, Box, Container, Card, CardContent } from "@mui/material";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Tabs from "../../components/Common/Tabs";
import Table from "../../components/Common/Table";
import Notification from "../../components/Common/Notification";
import LoadingDialog from "../../components/Form/LoadingDialog";
import CustomToolbar from "../../components/Common/CustomToolbar";
import RenderAddButton from "../../helpers/RenderButton/RenderAddButton";
import RenderPopOverButton from "../../helpers/RenderButton/RenderPopOverButton.js.js";
import { useCrud } from "../../store/crud";
import useListingQuery, { positiveInteger } from "../../helpers/useListingQuery";
import packagesModule from "../../store/packages/module";
import PackageDialog from "./PackageDialog";
import PackageTypeDialog from "./PackageTypeDialog";
import {
  currency, displayCategory, displayStatus, packageCategories, packageStatuses, packageTypes,
} from "./packagePresentation";
import "./packages.css";
export default function Packages() {
  const {
    list,
    create,
    delete: remove,
    reset,
    state,
    loading,
    notification,
    setNotification
  } = useCrud(packagesModule);
  const permissions = useSelector(root => root.Login.permissionArry) || [];
  const viewPermission = "view-package";
  const canView = permissions.includes(viewPermission);
  const canEdit = permissions.includes("edit-package");
  const navigate = useNavigate();
  const location = useLocation();
  const { query, update } = useListingQuery();
  const page = positiveInteger(query.get("page"));
  const length = [10, 20, 30, 40, 50].includes(Number(query.get("length"))) ? Number(query.get("length")) : 10;
  const search = query.get("search") || "";
  const status = query.get("status") || "";
  const type = query.get("type") || "";
  const category = query.get("category") || "";
  const [dialog, setDialog] = useState(null);
  const [content, setContent] = useState({
    open: false
  });
  const listing = state.list.data;
  const params = {
    page,
    length,
    search,
    status,
    type,
    category
  };
  useEffect(() => {
    list({
      page,
      length,
      search,
      status,
      type,
      category
    }, {
      preserveNotification: true
    });
  }, [list, page, length, search, status, type, category]);
  const categories = packageCategories;
  const filter = key => event => update({
    [key]: event.target.value === "all" ? "" : event.target.value
  }, {
    resetPage: true,
    replace: true
  });
  const openBookings = row => navigate(`/packages/bookings${row ? `?package_id=${encodeURIComponent(row.id)}&package_name=${encodeURIComponent(row.name || row.title || "")}` : ""}`, {
    state: {
      packagesReturnTo: location.pathname + location.search,
      packageName: row?.name || row?.title
    }
  });
  const refresh = () => list(params, {
    preserveNotification: true
  });
  const editPackage = row => navigate(`/edit-package/${encodeURIComponent(row.id)}`, {
    state: { packagesReturnTo: location.pathname + location.search },
  });
  const openCreate = () => setDialog({
    mode: "choose"
  });
  const proceedCreate = type => {
    if (["package", "informative", "privilege"].includes(type)) {
      setDialog(null);
      navigate(type === "package" ? "/add-package" : `/add-package?type=${type}`, {
        state: {
          packagesReturnTo: location.pathname + location.search
        }
      });
    } else {
      reset("create");
      setDialog({
        mode: "create",
        type
      });
    }
  };
  const confirmDelete = id => remove({
    id
  }, {
    successMessage: "Package deleted successfully",
    onSuccess: () => {
      setContent({
        open: false
      });
      if (page > 1 && listing?.rows?.length === 1) update({
        page: page - 1
      });else refresh();
    }
  });
  const columns = [{
    Header: "Package",
    id: "package",
    width: "29%",
    accessor: row => <Box className="package-name-cell">
      <button type="button" className="package-name" onClick={() => openBookings(row)} disabled={!canView}>{row.name || row.title || "Untitled package"}</button>
      <Stack direction="row" useFlexGap flexWrap="wrap" gap={1} mt={0.5}>
          {(row.category || row.category_id || row.category_name) && (
            <span className="package-chip package-chip-category">
              {displayCategory(row.category || row.category_id || row.category_name)}
            </span>
          )}
          {Number(row.discount_percentage) > 0 && <span className="package-chip package-chip-promotion">{row.discount_percentage}% OFF</span>}
      </Stack>
      </Box>
  }, {
    Header: "Package Type",
    id: "type",
    width: "14%",
    accessor: row => {
      const value = String(row.type || row.package_type || "package").toLowerCase();
      return <GeneralStatusLabel label={value} list={Helpers.packageTypes} />;
    }
  }, {
    Header: "Price",
    id: "price",
    width: "15%",
    accessor: row => <Stack gap={0.5}>
      <span>{row.price_from === true || row.price_from === 1 || row.is_from_price === true ? "From " : ""}{currency(row.price)}</span>
        {row.original_price != null && Number(row.original_price) > Number(row.price) && (
          <del className="package-old-price">{currency(row.original_price)}</del>
        )}
      </Stack>
  }, {
    Header: "Admin Email",
    id: "email",
    width: "17%",
    accessor: row => row.notification_email || "-"
  }, {
    Header: "Bookings",
    id: "bookings",
    width: "9%",
    accessor: row => row.bookings_count ?? row.booking_count ?? "-"
  }, {
    Header: "Status",
    id: "status",
    width: "10%",
    accessor: row => {
      const value = displayStatus(row.status);
      return <GeneralStatusLabel label={value} list={Helpers.packageStatuses} />;
    }
  }, {
    Header: "",
    id: "actions",
    width: "6%",
    accessor: row => <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={0.5}>
        {canEdit && <IconButton
        size="small" aria-label={`Edit ${row.name || row.title || "package"}`}
        onClick={() => editPackage(row)}
      >
        <i className="ri-pencil-line" />
      </IconButton>}
      <RenderPopOverButton
        viewPermission={viewPermission} delPermission="delete-package" viewLabel="View Bookings"
        viewIcon="ri-calendar-line" onView={() => openBookings(row)} menuWidth={200}
        onDel={() => setContent({
            open: true,
            id: row.id,
            data: row
        })}
      />
      </Stack>
  }];
  const counts = listing?.categoryCounts || {};
  const tabs = [{
    header: "All",
    tabID: "",
    count: counts.all ?? (category === "" ? listing?.total : undefined)
  }, ...categories.map(item => ({
    header: item.label,
    tabID: item.value,
    count: item.count ?? counts[item.value] ?? counts[item.label]
  }))];
  return <Box className="page-content packages-page">
    <Container
      maxWidth={false} disableGutters
      sx={{
        px: 1.5
      }}
    >
      <Breadcrumbs
        title="Packages"
        breadcrumbItems={[{
            title: "CVSKL",
            link: "/dashboard"
          }, {
            title: "Packages",
            link: "#"
        }]}
      />
      <LoadingDialog open={loading} />
      <Notification notification={notification} setNotification={setNotification} />
      <PackageDialog
        type="delete" content={content} setContent={setContent} data={content.data}
        onConfirm={confirmDelete} loading={state.delete.loading}
      />
      <Card
        variant="outlined"
        sx={{
          boxShadow: "none",
          borderColor: "#f0f1f3"
        }}
      >
        <CardContent
          sx={{
            p: {
              xs: 2,
              sm: "28px 20px 24px"
            },
            "&:last-child": {
              pb: 3
            },
            container: "packages-content / inline-size"
          }}
        >
          <CustomToolbar
            length={length}
            onLengthChange={nextLength => update({ length: nextLength }, { resetPage: true })}
            search={search}
            onSearchChange={value => update({ search: value }, { resetPage: true, replace: true })}
            searchAriaLabel="Search by package"
            filterList={[
              { type: "select", label: "Status", name: "status", value: status || "all",
                data: [{ value: "all", label: "All statuses" }, ...packageStatuses],
                onChange: value => update({ status: value === "all" ? "" : value }, { resetPage: true, replace: true }) },
              { type: "select", label: "Type", name: "type", value: type || "all",
                data: [{ value: "all", label: "All types" }, ...packageTypes],
                onChange: value => update({ type: value === "all" ? "" : value }, { resetPage: true, replace: true }) },
            ]}
            actions={<>
              <Button color="light" outline onClick={() => openBookings()} className="packages-bookings">
                View Bookings
                {listing?.bookingsCount != null && <span className="packages-booking-count">{listing.bookingsCount}</span>}
              </Button>
              <RenderAddButton permission="create-package" addButtonTite="Add Package" onClick={openCreate} />
            </>}
          />
          <Tabs
            defaultTab={category} activeTab={category}
            getActiveTab={value => update({
                category: value
              }, {
                resetPage: true
            })}
            content={tabs}
          />
          <Box className="packages-table management-table">
            <Table
              header={columns} data={listing?.rows || []} length={length} page={page}
              totalPage={listing?.lastPage || 1}
              getLengthPage={(nextPage, nextLength) => {
                if (nextPage !== page || Number(nextLength) !== length) update({
                    page: nextPage,
                    length: nextLength
                });
              }}
              onRowClick={canEdit ? editPackage : (canView ? openBookings : undefined)}
            />
          </Box>
        </CardContent>
      </Card>
        {dialog?.mode === "choose" && <PackageTypeDialog onClose={() => setDialog(null)} onProceed={proceedCreate} />}
    </Container>
    </Box>;
}
